
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { DetailedBotAccount } from '@/types/trader';
import { AlertCircle, Key, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { PremiumAddAccountDialog } from './PremiumAddAccountDialog';
import { usePremiumBotAccountValidation } from '@/hooks/use-premium-bot-account-validation';
import { toast } from 'sonner';
import { CTraderDisconnectedDialog } from '@/components/common/CTraderDisconnectedDialog';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { EnhancedPremiumTradingAccountCard } from './EnhancedPremiumTradingAccountCard';

interface EnhancedPremiumConnectedAccountsSectionProps {
  accounts: DetailedBotAccount[];
  isLoading: boolean;
  error: Error | null;
  botId: string;
  onToggleConnection: (accountId: string, isConnected: boolean) => void;
  onDeleteAccount: (accountId: string) => void;
  deletingAccountIds?: Set<string>;
  disconnectedCspAccountId?: string;
  isDisconnectedDialogOpen?: boolean;
  setIsDisconnectedDialogOpen?: (isOpen: boolean) => void;
}

export const EnhancedPremiumConnectedAccountsSection = ({
  accounts,
  isLoading,
  error,
  botId,
  onToggleConnection,
  onDeleteAccount,
  deletingAccountIds = new Set(),
  disconnectedCspAccountId,
  isDisconnectedDialogOpen = false,
  setIsDisconnectedDialogOpen = () => {}
}: EnhancedPremiumConnectedAccountsSectionProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [processingAccountIds, setProcessingAccountIds] = useState<Set<string>>(new Set());
  const { canAddAccount, getErrorMessage, currentCount, maxAccounts } = usePremiumBotAccountValidation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  const handleOpenAddDialog = () => {
    if (!canAddAccount()) {
      toast.error("Account Limit Reached", {
        description: getErrorMessage(),
      });
      return;
    }
    setIsAddDialogOpen(true);
  };

  const handleToggleConnection = async (accountId: string, isConnected: boolean) => {
    try {
      setProcessingAccountIds(prev => new Set([...prev, accountId]));
      await onToggleConnection(accountId, isConnected);
    } catch (error) {
      console.error('Error toggling connection:', error);
    } finally {
      setProcessingAccountIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    try {
      setProcessingAccountIds(prev => new Set([...prev, accountId]));
      await onDeleteAccount(accountId);
    } catch (error) {
      console.error('Error deleting account:', error);
    } finally {
      setProcessingAccountIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  if (isLoading) {
    return (
      <div className={`space-y-${isMobile ? "4" : "6"}`}>
        <div className="animate-pulse space-y-4">
          <div className="flex items-center gap-3">
            <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
              <div className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} bg-primary/20 rounded`} />
            </div>
            <div>
              <div className={`h-${isMobile ? "6" : "7"} w-48 bg-muted rounded`} />
              <div className={`h-4 w-64 bg-muted/60 rounded mt-1`} />
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className={`${isMobile ? "h-[300px]" : "h-[400px]"} rounded-lg bg-muted`} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-medium flex items-center">
            <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
            Error Loading Connected Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            There was a problem loading your connected accounts. Please try again later.
          </p>
        </CardContent>
      </Card>
    );
  }

  const EmptyStateCard = () => (
    <div className="col-span-full border-dashed border-2 border-muted bg-gradient-to-br from-muted/20 to-muted/10 rounded-xl">
      <div className={`flex flex-col items-center justify-center ${isMobile ? 'py-12 px-4' : 'py-16 px-6'} text-center`}>
        <div className={`${isMobile ? 'mb-4 p-4' : 'mb-6 p-6'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-full`}>
          <Key className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} text-primary`} />
        </div>
        <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-foreground mb-3`}>
          {t('no_accounts_connected')}
        </h3>
        <p className={`${isMobile ? 'text-sm' : 'text-sm'} text-muted-foreground ${isMobile ? 'mb-6' : 'mb-8'} max-w-md leading-relaxed`}>
          Connect your first trading account to start using this premium bot and monitor your trading activity.
        </p>
        <Button 
          onClick={handleOpenAddDialog}
          size={isMobile ? "sm" : "default"}
          className="group bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
        >
          <Plus className={`${isMobile ? 'h-4 w-4 mr-1' : 'h-5 w-5 mr-2'} transition-transform duration-200 group-hover:rotate-90`} />
          {isMobile ? 'Connect Account' : t('add_new_account')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className={`space-y-${isMobile ? "4" : "6"}`}>
      <div className="flex items-center gap-3">
        <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
          <Key className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} text-primary`} />
        </div>
        <div>
          <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold text-foreground`}>
            {t('connected_accounts')}
          </h2>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>
            Manage your trading account connections for this premium bot
          </p>
        </div>
      </div>

      <Separator className="bg-gradient-to-r from-transparent via-border to-transparent" />

      {!accounts.length ? (
        <EmptyStateCard />
      ) : (
        <div className={`grid grid-cols-1 ${isMobile ? 'gap-3' : 'md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
          {accounts.map((account) => (
            <EnhancedPremiumTradingAccountCard 
              key={account.id} 
              account={account}
              onToggleConnection={handleToggleConnection}
              onDeleteAccount={handleDeleteAccount}
              isProcessing={processingAccountIds.has(account.id)}
              isDeleting={deletingAccountIds.has(account.id)}
            />
          ))}
        </div>
      )}

      {/* Add New Account Button - Fixed position */}
      <div className="flex justify-center pt-4">
        <Button 
          variant="outline"
          onClick={handleOpenAddDialog}
          size={isMobile ? "sm" : "default"}
          className="border-dashed border-2 hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
          disabled={!canAddAccount()}
        >
          <Plus className={`${isMobile ? "mr-1 h-3.5 w-3.5" : "mr-2 h-4 w-4"}`} />
          {t('add_new_account')}
        </Button>
      </div>

      <PremiumAddAccountDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        botId={botId}
        onSuccess={() => setIsAddDialogOpen(false)}
      />

      <CTraderDisconnectedDialog
        open={isDisconnectedDialogOpen}
        onOpenChange={setIsDisconnectedDialogOpen}
        cspAccountId={disconnectedCspAccountId}
      />
    </div>
  );
};
